import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ClassTimetableGrid, ExamEvent, PeriodSlot, WEEKDAYS, Weekday } from '../models/schedule.model';

const LS_PERIODS = 'eschool-schedule-periods-v1';
const LS_EXAMS = 'eschool-schedule-exams-v1';
const lsTimetableKey = (c: string) => `eschool-schedule-timetable-${c}-v1`;

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {

  private readonly base = `${environment.apiUrl}/schedules`;

  constructor(private http: HttpClient) { }

  defaultPeriods(): PeriodSlot[] {
    const rows: { label: string; start: string; end: string }[] = [
      { label: 'Period 1', start: '08:00', end: '08:45' },
      { label: 'Period 2', start: '08:50', end: '09:35' },
      { label: 'Period 3', start: '09:40', end: '10:25' },
      { label: 'Break', start: '10:25', end: '10:40' },
      { label: 'Period 4', start: '10:40', end: '11:25' },
      { label: 'Period 5', start: '11:30', end: '12:15' },
      { label: 'Lunch', start: '12:15', end: '13:00' },
      { label: 'Period 6', start: '13:00', end: '13:45' },
      { label: 'Period 7', start: '13:50', end: '14:35' },
      { label: 'Period 8', start: '14:40', end: '15:25' }
    ];
    return rows.map((r, i) => ({
      id: `p${i + 1}`,
      order: i + 1,
      label: r.label,
      startTime: r.start,
      endTime: r.end
    }));
  }

  getPeriods(): Observable<PeriodSlot[]> {
    return this.http.get<PeriodSlot[]>(`${this.base}/periods`).pipe(
      map(p => (p && p.length ? p : this.readCachedPeriods())),
      tap(p => this.cachePeriods(p)),
      catchError(() => of(this.readCachedPeriods()))
    );
  }

  savePeriods(periods: PeriodSlot[]): Observable<void> {
    window.localStorage.setItem(LS_PERIODS, JSON.stringify(periods));
    return this.http.put<void>(`${this.base}/periods`, periods).pipe(
      catchError(() => of(undefined))
    );
  }

  getTimetableGrid(className: string, periodCount: number): Observable<ClassTimetableGrid> {
    return this.http.get<ClassTimetableGrid>(`${this.base}/timetable/${encodeURIComponent(className)}`).pipe(
      map(g => this.normalizeGrid(g, periodCount)),
      tap(g => window.localStorage.setItem(lsTimetableKey(className), JSON.stringify(g))),
      catchError(() => of(this.readCachedTimetable(className, periodCount)))
    );
  }

  saveTimetableGrid(grid: ClassTimetableGrid, periodCount: number): Observable<void> {
    const normalized = this.normalizeGrid(grid, periodCount);
    window.localStorage.setItem(lsTimetableKey(normalized.className), JSON.stringify(normalized));
    return this.http.put<void>(
      `${this.base}/timetable/${encodeURIComponent(normalized.className)}`,
      normalized
    ).pipe(
      catchError(() => of(undefined))
    );
  }

  getExams(): Observable<ExamEvent[]> {
    return this.http.get<ExamEvent[]>(`${this.base}/exams`).pipe(
      tap(e => window.localStorage.setItem(LS_EXAMS, JSON.stringify(e))),
      catchError(() => of(this.readCachedExams()))
    );
  }

  saveExams(exams: ExamEvent[]): Observable<void> {
    window.localStorage.setItem(LS_EXAMS, JSON.stringify(exams));
    return this.http.put<void>(`${this.base}/exams`, exams).pipe(
      catchError(() => of(undefined))
    );
  }

  emptyGrid(className: string, periodCount: number): ClassTimetableGrid {
    const subjects = {} as Record<Weekday, string[]>;
    for (const d of WEEKDAYS) {
      subjects[d] = new Array(periodCount).fill('');
    }
    return { className, subjects };
  }

  normalizeGrid(grid: ClassTimetableGrid, periodCount: number): ClassTimetableGrid {
    const subjects = {} as Record<Weekday, string[]>;
    for (const d of WEEKDAYS) {
      const row = (grid.subjects[d] || []).slice(0, periodCount);
      while (row.length < periodCount) {
        row.push('');
      }
      subjects[d] = row;
    }
    return { className: grid.className, subjects };
  }

  private cachePeriods(p: PeriodSlot[]): void {
    if (p && p.length) {
      window.localStorage.setItem(LS_PERIODS, JSON.stringify(p));
    }
  }

  private readCachedPeriods(): PeriodSlot[] {
    const raw = window.localStorage.getItem(LS_PERIODS);
    if (!raw) {
      return this.defaultPeriods();
    }
    try {
      const p = JSON.parse(raw) as PeriodSlot[];
      return p && p.length ? p : this.defaultPeriods();
    } catch {
      return this.defaultPeriods();
    }
  }

  private readCachedTimetable(className: string, periodCount: number): ClassTimetableGrid {
    const raw = window.localStorage.getItem(lsTimetableKey(className));
    if (!raw) {
      return this.emptyGrid(className, periodCount);
    }
    try {
      const g = JSON.parse(raw) as Partial<ClassTimetableGrid>;
      if (!g || g.className !== className) {
        return this.emptyGrid(className, periodCount);
      }
      return this.normalizeGrid(
        { className, subjects: (g.subjects || {}) as ClassTimetableGrid['subjects'] },
        periodCount
      );
    } catch {
      return this.emptyGrid(className, periodCount);
    }
  }

  private readCachedExams(): ExamEvent[] {
    const raw = window.localStorage.getItem(LS_EXAMS);
    if (!raw) {
      return [];
    }
    try {
      return JSON.parse(raw) as ExamEvent[];
    } catch {
      return [];
    }
  }
}
